import { DirectoryTypeConsts, ItemActionConsts } from 'client/util/Constants';
import DownloadUtil from 'client/util/DownloadUtil';
import JobApi from 'client/api/v2/JobApi';

export default {
  /**
   * This method converts a JSON object to a CSV
   * @param {Object} json - The JSON object to be converted
   * @param {Boolean} [options.alphabetize] - Whether to alphabetize columns
   * @param {Array} [options.fields] - Fields to use in the CSV
   * @param {Array} [options.fieldsToInclude] - Fields to include in the CSV (will be placed first)
   * @param {Boolean} [options.sanitize] - Sanitize values to prevent injection attack
   */
  convertJsonToCsv(json, options = {}) {
    // Replace null and undefined cell values with an empty string
    const replacer = (key, value) => (value === null || value === undefined ? '' : value);

    // Function to remove bad starting characters to sanitize against injection attack
    const removeBadStartingCharacters = (value) => {
      let sanitizedValue = value;
      const badStartingCharacters = ['=', '+', '-', '@', '\n', '\t'];
      let foundBadStartingCharacter = false;

      badStartingCharacters.forEach((badStartingCharacter) => {
        if (value.indexOf(badStartingCharacter) === 0) {
          sanitizedValue = value.slice(badStartingCharacter.length);
          foundBadStartingCharacter = true;
        }
      });
      if (foundBadStartingCharacter) {
        // Run through function again to make sure next starting character isn't bad either
        return removeBadStartingCharacters(sanitizedValue);
      }
      return sanitizedValue;
    };

    let header = [];
    if (options.fields instanceof Array) {
      // Use column fields provided (instead of figuring them out automatically)
      header = options.fields;
    } else {
      // Loop through each row and find all object keys
      header = json.reduce((acc, d) => {
        Object.keys(d).forEach((key) => {
          if (!acc.includes(key)) acc.push(key);
        });
        return acc;
      }, options.fieldsToInclude || []);
    }

    if (options.alphabetize) header.sort();

    let csv = json.map(row => (
      header.map((fieldName) => {
        // Remove all double-quotes in the value and then wrap the whole thing in double quotes
        let cellValue = JSON.stringify(row[fieldName], replacer).replace(/"/g, '');

        // Remove bad starting characters, if any
        if (options.sanitize) {
          cellValue = removeBadStartingCharacters(cellValue);
        }

        return `"${cellValue}"`;
      })
    ).join(','));

    csv.unshift(header.join(','));
    csv = csv.join('\r\n');
    return csv;
  },

  /**
   * @see https://stackoverflow.com/questions/17564103/using-javascript-to-download-file-as-a-csv-file#answer-17564369
   */
  downloadCsv(jobId, importedUsers, type, actionType) {
    const csv = this.convertJsonToCsv(importedUsers);

    const typeName = type === DirectoryTypeConsts.workday ? 'workday' : 'bulk';
    const actionName = actionType === ItemActionConsts.update ? 'update' : 'import';
    const fileName = `${typeName}-${actionName}-${jobId}.csv`;
    this.triggerCSVDownloadLink(fileName, csv);
  },

  downloadCsvTemplate(templateJson, templateName) {
    const csv = this.convertJsonToCsv(templateJson);

    const fileName = `${templateName}.csv`;
    this.triggerCSVDownloadLink(fileName, csv);
  },

  triggerCSVDownloadLink(fileName, csv) {
    DownloadUtil.downloadFile(csv, fileName);
  },

  getImportResults(jobId) {
    return new Promise((resolve, reject) => {
      JobApi
        .getImportResults(jobId)
        .then((importedUsers) => {
          if (importedUsers && importedUsers.length) {
            resolve(this.processImportedUsers(importedUsers));
          } else {
            reject(new Error('No imported users found!'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getImportResultsDetails(jobId) {
    return new Promise((resolve, reject) => {
      JobApi
        .getImportResultsDetails(jobId)
        .then((importedUsers) => {
          if (importedUsers && importedUsers.length) {
            resolve(this.processImportedUsersDetails(importedUsers));
          } else {
            reject(new Error('No imported users found.'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Map the imported users returned from the GET
   * /api/v2/workdays/{workdayId}/import/{jobId}/results response to a format
   * more suitable for a CSV download.
   */
  processImportedUsers(importedUsers) {
    return importedUsers.reduce((importedUsers2, importedUser) => {
      const { status, statusMsg, createdAt } = importedUser;
      const { systemUser } = importedUser.meta;
      const {
        attributes,
        email,
        firstname,
        lastname,
        username,
      } = systemUser;

      const importedUser2 = {
        email,
        username,
        firstname,
        lastname,
      };

      if (attributes && attributes.length) {
        attributes.forEach((attribute) => {
          const { name, value } = attribute;
          importedUser2[name] = value;
        });
      }

      // NOTE: These fields are added _after_ the attributes so that they are
      // the last columns in the CSV.
      importedUser2.status = status;
      importedUser2.statusMsg = statusMsg;
      importedUser2.created = createdAt;

      importedUsers2.push(importedUser2);

      return importedUsers2;
    }, []);
  },

  processImportedUsersDetails(usersToProcess) {
    return usersToProcess.reduce((importedUsers, importedUser) => {
      let email;
      const filteredChanges = [];
      const {
        operation, username, message_chain: messageChain, changes = [],
      } = importedUser;

      changes.forEach(change => {
        if (change?.to && change?.to?.length > 0) {
          filteredChanges.push(change);
        }
        if (change?.field === 'email') {
          email = change.to;
        }
      });

      const processedUser = {
        operation, username, email, changes: filteredChanges, messageChain,
      };

      importedUsers.push(processedUser);

      return importedUsers;
    }, []);
  },
};
